import type { CSSProperties, JSX } from 'react';
import { Button, Descriptions, Modal, Popconfirm, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import type { ClinicalRecord } from '@erp/clinical-history/api/clinicalHistory.api';
import RichTextDisplay from '@tools/components/RichTextDisplay.tsx';
import { getFormattedDuration } from '@tools/utils/duration.ts';

import {
  buildClinicalRecordSpaceLabel,
  buildClinicalRecordStaffLabel,
  buildServiceLinesLabel,
} from './historyHelpers';

const { Text } = Typography;

const noteBoxStyle: CSSProperties = {
  padding: '12px 16px',
  background: 'rgba(0,0,0,0.02)',
  borderRadius: 8,
  border: '1px solid rgba(5,5,5,0.06)',
  minHeight: 60,
};

interface ClinicalRecordHistoryModalProps {
  record: ClinicalRecord | null;
  canEdit: boolean;
  canDelete: boolean;
  deleteLoading: boolean;
  spaceNameById?: Map<number, string>;
  onClose: () => void;
  onEdit: (record: ClinicalRecord) => void;
  onDelete: (record: ClinicalRecord) => void;
}

export function ClinicalRecordHistoryModal({
  record,
  canEdit,
  canDelete,
  deleteLoading,
  spaceNameById,
  onClose,
  onEdit,
  onDelete,
}: ClinicalRecordHistoryModalProps): JSX.Element {
  const canMutateRecord = Boolean(record?.latest_history_entry_id);

  return (
    <Modal
      title="Historia clínica"
      open={Boolean(record)}
      onCancel={onClose}
      footer={
        <Space>
          {record && canDelete && canMutateRecord ? (
            <Popconfirm
              title="Eliminar entrada de historial"
              description="Esta acción no se puede deshacer"
              okText="Eliminar"
              okButtonProps={{ danger: true, loading: deleteLoading }}
              cancelText="Cancelar"
              disabled={deleteLoading}
              onConfirm={() => onDelete(record)}
            >
              <Button danger loading={deleteLoading}>
                Eliminar
              </Button>
            </Popconfirm>
          ) : null}
          {record && canEdit && canMutateRecord ? (
            <Button onClick={() => onEdit(record)}>Editar</Button>
          ) : null}
          <Button type="primary" onClick={onClose}>
            Cerrar
          </Button>
        </Space>
      }
    >
      {record ? (
        <Space orientation="vertical" style={{ width: '100%' }} size="small">
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Fecha">
              {dayjs(record.start_at).format('DD/MM/YYYY HH:mm')} hs
            </Descriptions.Item>
            <Descriptions.Item label="Duración">
              {getFormattedDuration(dayjs(record.end_at).diff(dayjs(record.start_at), 'minute'))}
            </Descriptions.Item>
            <Descriptions.Item label="Profesional / Sector">
              <Tag color="cyan">{buildClinicalRecordStaffLabel(record)}</Tag> /
              <Tag color="purple">{buildClinicalRecordSpaceLabel(record, spaceNameById)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Servicios">
              {record.service_lines.length > 0
                ? record.service_lines.map((line) => (
                    <Tag key={line.id ?? line.service_id}>
                      {line.service_name ?? `Serv.#${line.service_id}`}
                    </Tag>
                  ))
                : buildServiceLinesLabel(record.service_lines)}
            </Descriptions.Item>
            {record.latest_history_recorded_by ? (
              <Descriptions.Item label="Registrado por">
                <Tag>{record.latest_history_recorded_by}</Tag>
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Nota de la historia
            </Text>
            <div style={noteBoxStyle}>
              <RichTextDisplay value={record.latest_history_note} />
            </div>
          </div>
        </Space>
      ) : null}
    </Modal>
  );
}

export default ClinicalRecordHistoryModal;
